// What an open pipeline record must carry while it is still a LEAD (client,
// 2026-10-28): the product the customer asked about, the details of the person
// the salesperson actually spoke to, and a next follow-up. IndiaMART leads
// arrive with none of these, and employees were leaving them that way. Once a
// quotation, proforma, sales order or invoice has been raised for it
// (2026-10-29), its work moves to that document — whose own follow-ups carry
// it — and this no longer applies. The server applies the same rule
// (src/shared/pipeline/lead-completeness.js) and the Sales Performance
// "Daily work" monitor counts it (src/shared/sales-performance/work-monitor.js).
import type { PipelineRecord } from './types';

const CLOSED = ['closed_won', 'closed_lost'];

export type LeadWorkGap = 'product' | 'contact' | 'followup';

/** The follow-up fields that say who was spoken to. */
export interface FollowupContact {
  status?: string;
  outcome?: string;
  /** Closed as "No next follow-up needed" — why (2026-10-30). */
  noNextReason?: string;
  contactPersonName?: string;
  contactPersonPhone?: string;
}

export function isOpenRecord(record: Pick<PipelineRecord, 'stage'>): boolean {
  return !CLOSED.includes(String(record.stage || ''));
}

export function hasProduct(record: Pick<PipelineRecord, 'lines' | 'qualification_product' | 'qualification_product_id' | 'hasIssuedQuotation'>): boolean {
  // A sent quotation names the product — and freezes the record's own lines,
  // so asking for them again would leave the person stuck.
  if (record.hasIssuedQuotation) return true;
  if ((record.lines || []).some((l) => l.productId || (l.productName || '').trim() || (l.description || '').trim())) return true;
  return Boolean(record.qualification_product_id || (record.qualification_product || '').trim());
}

/** Some follow-up on the record names the person spoken to, with a number. */
export function hasContactDetails(followups: FollowupContact[]): boolean {
  return followups.some((f) => String(f.contactPersonName || '').trim() && String(f.contactPersonPhone || '').trim());
}

/** What is still missing: [] when the record is complete. */
export function leadWorkGaps(
  record: Pick<PipelineRecord, 'stage' | 'lines' | 'qualification_product' | 'qualification_product_id' | 'hasIssuedQuotation' | 'hasSalesDocument'>,
  followups: FollowupContact[],
): LeadWorkGap[] {
  if (!isOpenRecord(record)) return [];
  if (record.hasSalesDocument || record.hasIssuedQuotation) return [];
  const gaps: LeadWorkGap[] = [];
  if (!hasProduct(record)) gaps.push('product');
  if (!hasContactDetails(followups)) gaps.push('contact');
  // Closed as "Order received": the next step is the quotation itself (2026-10-29).
  // Closed as "No next follow-up needed", with a reason: the chase ends (2026-10-30).
  if (!followups.some((f) => f.status === 'open' || f.outcome === 'order_received' || (f.noNextReason || '').trim())) gaps.push('followup');
  return gaps;
}
